import { useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

async function uploadToCloudinary(file, folder = "projects") {
  const sig = await api.get(`/cloudinary/signature`, { params: { folder } }).then((r) => r.data);
  const form = new FormData();
  form.append("file", file);
  form.append("api_key", sig.api_key);
  form.append("timestamp", sig.timestamp);
  form.append("signature", sig.signature);
  form.append("folder", sig.folder);
  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
    { method: "POST", body: form }
  );
  if (!res.ok) throw new Error("Cloudinary upload failed");
  const data = await res.json();
  return data.secure_url;
}

/**
 * Props:
 * - value: string | string[]   (single URL for cover, array of URLs for gallery)
 * - onChange: (newValue) => void
 * - multiple?: boolean
 * - label: string
 * - testid?: string
 */
export default function ImageUploader({ value, onChange, multiple = false, label, testid }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const items = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        try {
          const url = await uploadToCloudinary(f);
          urls.push(url);
        } catch (e) {
          toast.error(`Failed: ${f.name}`);
        }
      }
      if (urls.length === 0) return;
      if (multiple) {
        onChange([...(Array.isArray(value) ? value : []), ...urls]);
      } else {
        onChange(urls[0]);
      }
      toast.success(`Uploaded ${urls.length} image${urls.length > 1 ? "s" : ""}.`);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => {
    if (multiple) {
      onChange(items.filter((_, i) => i !== idx));
    } else {
      onChange("");
    }
  };

  const moveItem = (from, to) => {
    if (!multiple) return;
    if (to < 0 || to >= items.length) return;
    const arr = [...items];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    onChange(arr);
  };

  return (
    <div data-testid={testid}>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-muteInk mb-2">{label}</label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer border-2 border-dashed p-6 text-center transition-colors duration-200 ${
          dragging ? "border-ink bg-stone2/40" : "border-borderWarm hover:border-ink/60"
        }`}
        data-testid={testid ? `${testid}-dropzone` : undefined}
      >
        <p className="text-[11px] uppercase tracking-[0.24em] text-muteInk">
          {uploading ? "Uploading…" : `Drop ${multiple ? "images" : "an image"} here or click to browse`}
        </p>
        <p className="text-[10px] text-muteInk/70 mt-2">PNG · JPG · WebP</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          data-testid={testid ? `${testid}-input` : undefined}
        />
      </div>

      {items.length > 0 && (
        <div className={`mt-4 grid ${multiple ? "grid-cols-3 md:grid-cols-4" : "grid-cols-1"} gap-3`}>
          {items.map((url, i) => (
            <div key={`${url}-${i}`} className="relative group border border-borderWarm">
              <img src={url} alt="" className="w-full h-24 object-cover" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-ink/60 flex items-center justify-center gap-1">
                {multiple && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); moveItem(i, i - 1); }}
                      className="text-bone bg-ink/70 hover:bg-clay text-[10px] px-2 py-1 uppercase tracking-wider"
                      title="Move left"
                    >←</button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); moveItem(i, i + 1); }}
                      className="text-bone bg-ink/70 hover:bg-clay text-[10px] px-2 py-1 uppercase tracking-wider"
                      title="Move right"
                    >→</button>
                  </>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                  data-testid={testid ? `${testid}-remove-${i}` : undefined}
                  className="text-bone bg-clay hover:bg-ink text-[10px] px-2 py-1 uppercase tracking-wider"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
